import { useCallback, useEffect, useRef, useState } from 'react'
import { API_BASE_URL } from '../lib/http.js'
import { clockViewApi } from '../lib/api.js'
import { bestClockSample, serverNow, type ClockAnchor, type ClockSample } from '../lib/clock.js'
import type { ClockBootstrap } from '../types/domain.js'

export interface ClockSyncState {
  nowMs: number
  rtt: number | null
  ageMs: number | null
  dataAgeMs: number | null
  stale: boolean
  wsConnected: boolean
}

export function useClockRuntime(displayId: string, token: string | null) {
  const [bootstrap, setBootstrap] = useState<ClockBootstrap | null>(null)
  const [error, setError] = useState('')
  const [wsConnected, setWsConnected] = useState(false)
  const [stateVersion, setStateVersion] = useState(0)
  const [nowMs, setNowMs] = useState(Date.now())
  const anchorRef = useRef<ClockAnchor | null>(null)
  const freshTimeRef = useRef(false)
  const lastDataRef = useRef(0)
  const wsRef = useRef<WebSocket | null>(null)
  const loadSequenceRef = useRef(0)
  // Conducteur courant, lu par le gestionnaire de socket sans le recréer.
  const itemsRef = useRef<readonly { id: string }[]>([])

  const measure = useCallback(async (count = 1) => {
    if (!token) return false
    const samples: ClockSample[] = []
    for (let index = 0; index < count; index += 1) {
      const clientStart = performance.now()
      try {
        const response = await clockViewApi.time(displayId, token, clientStart)
        samples.push({ clientStart, clientEnd: performance.now(), serverTime: new Date(response.serverTime).getTime() })
      } catch { /* un échantillon manquant ne remplace jamais l'ancre valide */ }
    }
    const anchor = bestClockSample(samples)
    if (!anchor) return false
    anchorRef.current = anchor
    freshTimeRef.current = true
    setStateVersion(version => version + 1)
    return true
  }, [displayId, token])

  const load = useCallback(async () => {
    if (!token) return
    // Deux `GRID_UPDATED` rapprochés lancent deux rechargements : seul le plus
    // récent a le droit d'écrire, sinon une réponse lente réaffiche l'ancien
    // conducteur par-dessus l'ajustement qui vient d'être fait.
    const sequence = ++loadSequenceRef.current
    try {
      freshTimeRef.current = false
      const [data, synchronized] = await Promise.all([clockViewApi.bootstrap(displayId, token), measure(anchorRef.current ? 1 : 5)])
      if (sequence !== loadSequenceRef.current) return
      setBootstrap(data)
      itemsRef.current = data.items
      lastDataRef.current = performance.now()
      setError('')
      if (!synchronized && !anchorRef.current) setError('Synchronisation temporelle indisponible')

      // Une socket vivante est conservée : la fermer à chaque rechargement
      // perdait tout événement émis pendant la reconnexion. Le ticket inutilisé
      // expire seul côté serveur.
      const open = wsRef.current
      if (open && open.readyState <= WebSocket.OPEN) {
        if (open.readyState === WebSocket.OPEN) freshTimeRef.current = synchronized
        return
      }
      const api = new URL(API_BASE_URL, window.location.origin)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const ws = new WebSocket(`${protocol}//${api.host}/ws/clock/${encodeURIComponent(displayId)}?ticket=${encodeURIComponent(data.wsTicket)}`)
      wsRef.current = ws
      ws.onopen = () => { freshTimeRef.current = synchronized; setWsConnected(true) }
      ws.onmessage = event => {
        const message = JSON.parse(String(event.data)) as { type?: string; displayId?: string; itemId?: string; status?: string; data?: Record<string, unknown> }
        if (message.type === 'PING' || message.type === 'CLOCK_READY') return
        lastDataRef.current = performance.now()
        if (message.type === 'CLOCK_DISPLAY_REVOKED' && message.displayId === displayId) {
          setError('Lien Clock désactivé')
          ws.close()
          return
        }
        if (message.type === 'PLAYOUT_UPDATE') {
          setBootstrap(current => current ? { ...current, playout: { ...(current.playout ?? {}), ...(message.data ?? {}) } } : current)
          return
        }
        if (message.type === 'ITEM_STATUS' && message.itemId && message.status) {
          // Un statut qui vise un élément absent du conducteur chargé est celui
          // d'un élément tout juste créé — reprise de raccrochage, comblage —
          // qui a doublé le rechargement de `GRID_UPDATED`. On recharge plutôt
          // que de le perdre : c'est ce qui figeait le compteur de la partie 2.
          if (!itemsRef.current.some(item => item.id === message.itemId)) { void load(); return }
          setBootstrap(current => current ? { ...current, items: current.items.map(item => item.id === message.itemId ? { ...item, status: message.status as typeof item.status } : item) } : current)
          return
        }
        if (['GRID_UPDATED', 'GRIDS_UPDATED', 'CLOCK_LAYOUT_PUBLISHED', 'CLOCK_DISPLAY_UPDATED'].includes(message.type ?? '')) void load()
      }
      ws.onclose = () => {
        if (wsRef.current !== ws) return
        freshTimeRef.current = false
        setWsConnected(false)
      }
    } catch (caught) {
      if (sequence !== loadSequenceRef.current) return
      setError(caught instanceof Error ? caught.message : 'Clock indisponible')
      setWsConnected(false)
    }
  }, [displayId, measure, token])

  useEffect(() => {
    void load()
    return () => {
      // Un rechargement encore en vol ne doit pas rouvrir de socket après coup.
      loadSequenceRef.current += 1
      wsRef.current?.close()
    }
  }, [load])
  useEffect(() => {
    if (!token) return
    const timer = window.setInterval(() => { void measure(1) }, 10_000)
    return () => window.clearInterval(timer)
  }, [measure, token])
  useEffect(() => {
    let frame = 0
    let previousTenth = -1
    const tick = () => {
      const monotonicNow = performance.now()
      const current = anchorRef.current ? serverNow(anchorRef.current, monotonicNow) : Date.now()
      const tenth = Math.floor(current / 100)
      // rAF pilote le rythme, mais React ne rend que si le dixième visible a
      // changé — jamais à chaque frame écran.
      if (tenth !== previousTenth) { previousTenth = tenth; setNowMs(current) }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [])
  useEffect(() => {
    if (!token || wsConnected) return
    const retry = window.setInterval(() => { void load() }, 5_000)
    return () => window.clearInterval(retry)
  }, [load, token, wsConnected])

  const anchor = anchorRef.current
  const stale = !wsConnected || !anchor || !freshTimeRef.current
  return {
    bootstrap, error, reload: load,
    sync: { nowMs, rtt: anchor?.rtt ?? null, ageMs: anchor ? Date.now() - anchor.measuredAt : null, dataAgeMs: lastDataRef.current ? performance.now() - lastDataRef.current : null, stale, wsConnected } satisfies ClockSyncState,
    stateVersion,
  }
}
